import { askConfirm, escapeHtml } from '@colplat/ui-web';
import type { RefusedEntry } from '../webledgerClient.js';

export interface RefusedBannerOptions {
  entries: () => readonly RefusedEntry[];
  onChange: (fn: () => void) => () => void;
  discard: (outboxId: string) => Promise<void>;
  /** Open the entry to change it; saving it again sends it again. Absent where the page cannot. */
  open?: (recordId: string) => void;
}

/**
 * Entries saved on this device that the server would not accept as they are
 * (ADR-0011, 2026-10-21).
 *
 * Asked for: "add a visible state". The row says "not accepted"; this says
 * which entry, the server's reason in its own words, and the two ways out —
 * open it and change it (saving queues it afresh), or throw the unsent
 * version away. Until then it stays on the device and is not sent again:
 * retrying would only earn the same answer, and dropping it would lose work.
 */
export function mountRefusedBanner(host: HTMLElement, opts: RefusedBannerOptions): void {
  function render(): void {
    const list = opts.entries();
    if (list.length === 0) { host.hidden = true; host.innerHTML = ''; return; }
    host.hidden = false;
    host.innerHTML = list.map((r) => {
      const what = r.description || (r.op === 'create' ? 'A new entry' : 'An entry');
      const when = r.txDate ? ` (${escapeHtml(r.txDate)})` : '';
      return `<div class="alert alert-danger py-2 px-3 mb-2 small d-flex flex-wrap align-items-center gap-2" role="status">
        <span class="flex-grow-1">
          <span aria-hidden="true">⚠</span>
          <strong>${escapeHtml(what)}</strong>${when} was saved on this device but not accepted by the server:
          ${escapeHtml(r.message.replace(/\.\s*$/, ''))}. It is kept here and not sent again until you change it or discard it.
        </span>
        ${opts.open ? `<button class="btn btn-sm btn-outline-secondary" type="button" data-refused-open="${escapeHtml(r.recordId)}">Open</button>` : ''}
        <button class="btn btn-sm btn-link" type="button" data-refused-discard="${escapeHtml(r.outboxId)}">Discard my version</button>
      </div>`;
    }).join('');
  }

  host.addEventListener('click', (event) => {
    const target = event.target as HTMLElement;
    const open = target.closest<HTMLElement>('[data-refused-open]');
    if (open && opts.open) { opts.open(open.dataset['refusedOpen']!); return; }
    const discard = target.closest<HTMLElement>('[data-refused-discard]');
    if (!discard) return;
    const entry = opts.entries().find((r) => r.outboxId === discard.dataset['refusedDiscard']);
    if (!entry) return;
    void (async () => {
      const go = await askConfirm({
        title: 'Discard your unsent version?',
        message: entry.op === 'create'
          ? 'This entry was never on the server, so it will be gone.'
          : 'The entry goes back to what the server has. What you changed on this device is lost.',
        confirm: 'Discard it', cancel: 'Keep it', tone: 'danger',
      });
      if (go) await opts.discard(entry.outboxId);
    })();
  });

  opts.onChange(render);
  render();
}
